import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc,
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Lead } from '../models/Lead';
import { Business } from '../models/Business';

// Collection names in Firestore
const LEADS_COLLECTION = 'leads';
const BUSINESSES_COLLECTION = 'businesses';

// Batch size for IN queries to stay within Firebase limits
const BATCH_SIZE = 30;

/**
 * Create or update a business in Firestore
 * @param businessId The Google Places ID of the business
 * @param businessData Business data to save
 */
export const createOrUpdateBusiness = async (businessId: string, businessData: Partial<Business>): Promise<void> => {
  try {
    const businessRef = doc(db, BUSINESSES_COLLECTION, businessId);
    const docSnap = await getDoc(businessRef);
    
    if (docSnap.exists()) {
      // Update existing business
      await updateDoc(businessRef, {
        ...businessData,
        last_updated: serverTimestamp()
      });
    } else {
      // Create new business
      await setDoc(businessRef, {
        id: businessId,
        ...businessData,
        created_at: serverTimestamp(),
        last_updated: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error creating/updating business:', error);
    throw error;
  }
};

/**
 * Add a new lead to Firestore
 * @param leadData Lead data to add
 * @returns The ID of the newly created lead
 */
export const addLead = async (leadData: Omit<Lead, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    // First, create or update the business document
    if (leadData.businessId) {
      await createOrUpdateBusiness(leadData.businessId, {
        name: leadData.company,
        website: leadData.website,
        phone: leadData.phone,
        city: leadData.city,
        state: leadData.state,
        industryType: leadData.industryType
      });
    }

    const dataWithTimestamps = {
      ...leadData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, LEADS_COLLECTION), dataWithTimestamps);
    return docRef.id;
  } catch (error) {
    console.error('Error adding lead:', error);
    throw error;
  }
};

/**
 * Get all leads from Firestore
 * @returns Array of leads with business data
 */
export const getAllLeads = async (): Promise<Lead[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, LEADS_COLLECTION));
    const leads = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        notes: data.notes?.map((note: any) => ({
          ...note,
          createdAt: note.createdAt?.toDate() || new Date()
        }))
      } as Lead;
    });

    // Fetch business data for each lead
    const leadsWithBusiness = await Promise.all(
      leads.map(async (lead) => {
        if (lead.businessId) {
          const business = await getBusinessForLead(lead.businessId);
          if (business) {
            // Copy relevant business data to the lead
            return {
              ...lead,
              business,
              city: business.city,
              state: business.state,
              industryType: business.industryType
            };
          }
        }
        return lead;
      })
    );

    return leadsWithBusiness;
  } catch (error) {
    console.error('Error getting leads:', error);
    throw error;
  }
};

/**
 * Update a lead in Firestore
 * @param id Lead ID
 * @param leadData Lead data to update
 * @returns Promise that resolves when the update is complete
 */
export const updateLead = async (id: string, leadData: Partial<Omit<Lead, 'id' | 'createdAt' | 'updatedAt'>>): Promise<void> => {
  try {
    const leadRef = doc(db, LEADS_COLLECTION, id);
    const dataWithTimestamps = {
      ...leadData,
      updatedAt: serverTimestamp()
    };

    await updateDoc(leadRef, dataWithTimestamps);
  } catch (error) {
    console.error('Error updating lead:', error);
    throw error;
  }
};

/**
 * Delete a lead from Firestore
 * @param id Lead ID
 * @returns Promise that resolves when the deletion is complete
 */
export const deleteLead = async (id: string): Promise<void> => {
  try {
    const leadRef = doc(db, LEADS_COLLECTION, id);
    await deleteDoc(leadRef);
  } catch (error) {
    console.error('Error deleting lead:', error);
    throw error;
  }
};

/**
 * Get leads by a specific field value
 * @param field Field name to query
 * @param value Field value to match
 * @returns Array of leads matching the query
 */
export const getLeadsByField = async (field: keyof Lead, value: any): Promise<Lead[]> => {
  try {
    const q = query(collection(db, LEADS_COLLECTION), where(field, '==', value));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        notes: data.notes?.map((note: any) => ({
          ...note,
          createdAt: note.createdAt?.toDate() || new Date()
        }))
      } as Lead;
    });
  } catch (error) {
    console.error(`Error getting leads by ${field}:`, error);
    throw error;
  }
};

/**
 * Get a lead by its ID
 * @param id Lead ID
 * @returns The lead if found, null otherwise
 */
export const getLeadById = async (id: string): Promise<Lead | null> => {
  try {
    const docRef = doc(db, LEADS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        notes: data.notes?.map((note: any) => ({
          ...note,
          createdAt: note.createdAt?.toDate() || new Date()
        }))
      } as Lead;
    }
    return null;
  } catch (error) {
    console.error('Error fetching lead:', error);
    return null;
  }
};

export const getBusinessForLead = async (businessId: string): Promise<Business | null> => {
  try {
    const docRef = doc(db, BUSINESSES_COLLECTION, businessId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as Business;
    }
    return null;
  } catch (error) {
    console.error('Error fetching business:', error);
    return null;
  }
};

export const getLeadsByIndustry = async (industryId: string): Promise<Lead[]> => {
    try {
        const leadsRef = collection(db, LEADS_COLLECTION);
        const q = query(leadsRef, where('industryIds', 'array-contains', industryId));
        const querySnapshot = await getDocs(q);
        
        const leads: Lead[] = [];
        for (const doc of querySnapshot.docs) {
            const lead = { id: doc.id, ...doc.data() } as Lead;
            leads.push(lead);
        }
        
        return leads;
    } catch (error) {
        console.error('Error getting leads by industry:', error);
        throw error;
    }
};

/**
 * Get leads for multiple business IDs
 * @param businessIds Array of business IDs to check
 * @returns Map of business IDs to Lead objects
 */
export const getLeadsByBusinessIds = async (businessIds: string[]): Promise<Map<string, Lead>> => {
  try {
    const leadMap = new Map<string, Lead>();
    
    // Process businessIds in chunks of BATCH_SIZE
    for (let i = 0; i < businessIds.length; i += BATCH_SIZE) {
      const chunk = businessIds.slice(i, i + BATCH_SIZE);
      
      // Query for leads with the current chunk of business IDs
      const q = query(collection(db, LEADS_COLLECTION), where('businessId', 'in', chunk));
      const querySnapshot = await getDocs(q);
      
      // Add leads to the map
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.businessId) {
          leadMap.set(data.businessId, {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
            notes: data.notes?.map((note: any) => ({
              ...note,
              createdAt: note.createdAt?.toDate() || new Date()
            }))
          } as Lead);
        }
      });
    }
    
    return leadMap;
  } catch (error) {
    console.error('Error getting leads by business IDs:', error);
    throw error;
  }
}; 